<template>
    <div class="relative w-full p-0">
        <div
            class="relative flex w-full cursor-pointer select-none"
            :class="{
                'h-input-small': small,
                'h-input-xs': extraSmall,
                'h-input': !small && !extraSmall,
            }"
        >
            <input
                :id="vid"
                ref="input"
                :data-cy="vid"
                :disabled="disabled"
                :readonly="readonly"
                :name="vid"
                :type="inputType"
                :inputmode="inputType === 'number' ? 'decimal' : undefined"
                class="loading peer block h-full w-full rounded-input border px-3 py-2 text-sm text-muval-gray-1 placeholder-transparent hover:border-muval-gray-3 focus:border-focus focus:outline-none focus:ring-0"
                :class="{
                    'border-error': errorMessage,
                    'border-muval-gray-4': !errorMessage,
                    'bg-white': !errorMessage,
                    '!placeholder-gray-400': !label && placeholder,
                    '!bg-muval-gray-5': disabled || readonly,
                    '!px-1.5': extraSmall,
                    'muval-number-input': inputType === 'number',
                    'posthog-sensitive': censor,
                }"
                :placeholder="placeholder ? placeholder : label"
                :title="placeholder ? placeholder : label"
                :value="inputValue"
                :autocomplete="autocomplete"
                :autofocus="autofocus"
                :maxlength="maxlength"
                :style="inputRightPaddingStyle"
                @change="(e) => (!updateOnInput ? handleChange(e) : null)"
                @input="(e) => (updateOnInput ? handleChange(e) : null)"
                @blur="onBlur"
                @focus="onFocus"
            />
            <div
                v-if="label"
                class="peer pointer-events-none absolute -top-2 left-2 z-1 line-clamp-1 text-xs text-gray-400 transition-all duration-75 peer-placeholder-shown:top-[0.6125rem] peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-400 peer-focus:-top-2 peer-focus:text-xs peer-focus:text-focus skeleton:hidden peer-placeholder-shown:[&>div]:hidden peer-focus:[&>div]:block"
                :class="[
                    errorMessage ? 'text-error' : '',
                    infoType && !cubic && !square ? 'peer-focus:right-auto md:right-16' : '',
                    hasInputValue ? '!right-auto' : '',
                ]"
            >
                <span class="z-1 px-1">{{ label }}</span>
                <!--
                    The notch that breaks the input's border behind the floated label. It is anchored
                    to this wrapper rather than to the label text because the wrapper's geometry is
                    the same in every engine, while an inline span's box sits a fraction lower in
                    WebKit and leaves the top of the border showing through the label. The wrapper
                    is stretched to `md:right-16` while an `info-type` suffix is present, so while
                    the label rests inside an empty, unfocused field the full-width bar would paint
                    a white line straight through that suffix (CU-86d45pvyd). The wrapper hides it
                    in that state and shows it again the moment the label floats on focus.
                -->
                <div
                    class="absolute top-2 -z-1 h-0.5 w-full px-2 dark:bg-gray-900"
                    :class="disabled ? 'bg-muval-gray-5' : 'bg-white'"
                ></div>
            </div>

            <div
                v-if="cubic"
                :class="errorMessage ? 'border-error' : 'border-[#D0D0D0]'"
                class="peer pointer-events-none absolute right-0 top-0 z-0 h-full w-1/5"
            >
                <div class="absolute right-0 p-2 align-middle text-sm text-gray-900">
                    <span>m&sup3;</span>
                </div>
            </div>
            <div
                v-if="square"
                :class="errorMessage ? 'border-error' : 'border-[#D0D0D0]'"
                class="peer pointer-events-none absolute right-0 top-0 z-0 h-full w-1/5"
            >
                <div class="absolute right-0 p-2 align-middle text-sm text-gray-900">
                    <span>m&sup2;</span>
                </div>
            </div>

            <div
                v-if="infoType"
                ref="infoTypeEl"
                :class="[errorMessage ? 'border-error' : 'border-[#D0D0D0]', infoTypeClass]"
                class="peer pointer-events-none absolute right-0 top-0 z-0 flex h-full w-auto px-4 text-muval-gray-1"
            >
                <div class="relative my-auto mr-auto">
                    <span class="text-sm">{{ infoType }}</span>
                    <div
                        v-if="errorMessage"
                        class="absolute -left-5 top-1.5"
                    >
                        <svg
                            width="14"
                            height="13"
                            viewBox="0 0 14 13"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            class="text-error"
                        >
                            <path
                                d="M7.24598 9.71591L5.92852 9.71591L5.92852 8.42046L7.24598 8.42046L7.24598 9.71591ZM7.24598 7.125L5.92852 7.125L5.92852 3.23864L7.24598 3.23864L7.24598 7.125ZM6.58725 12.9546C10.2234 12.9546 13.1746 10.0527 13.1746 6.47728C13.1746 2.90182 10.2234 -7.30522e-07 6.58725 -1.0431e-06C2.95106 -1.35567e-06 -5.34657e-05 2.90182 -5.37836e-05 6.47727C-5.41014e-05 10.0527 2.95106 12.9546 6.58725 12.9546ZM6.58725 1.29545C9.49226 1.29545 11.8571 3.6208 11.8571 6.47728C11.8571 9.33375 9.49226 11.6591 6.58725 11.6591C3.68225 11.6591 1.31741 9.33375 1.31741 6.47727C1.31741 3.6208 3.68225 1.29545 6.58725 1.29545Z"
                                fill="currentColor"
                            />
                        </svg>
                    </div>
                </div>
            </div>

            <div
                v-if="errorMessage && !infoType && !cubic"
                class="absolute right-2 top-3"
            >
                <svg
                    width="14"
                    height="13"
                    viewBox="0 0 14 13"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    class="text-error"
                >
                    <path
                        d="M7.24598 9.71591L5.92852 9.71591L5.92852 8.42046L7.24598 8.42046L7.24598 9.71591ZM7.24598 7.125L5.92852 7.125L5.92852 3.23864L7.24598 3.23864L7.24598 7.125ZM6.58725 12.9546C10.2234 12.9546 13.1746 10.0527 13.1746 6.47728C13.1746 2.90182 10.2234 -7.30522e-07 6.58725 -1.0431e-06C2.95106 -1.35567e-06 -5.34657e-05 2.90182 -5.37836e-05 6.47727C-5.41014e-05 10.0527 2.95106 12.9546 6.58725 12.9546ZM6.58725 1.29545C9.49226 1.29545 11.8571 3.6208 11.8571 6.47728C11.8571 9.33375 9.49226 11.6591 6.58725 11.6591C3.68225 11.6591 1.31741 9.33375 1.31741 6.47727C1.31741 3.6208 3.68225 1.29545 6.58725 1.29545Z"
                        fill="currentColor"
                    />
                </svg>
            </div>
        </div>
        <div
            class="pointer-events-none absolute -bottom-5 whitespace-nowrap text-xxs"
            :class="[wideError ? 'w-max' : 'w-full truncate', errorMessage ? 'text-error' : '']"
        >
            <span
                class="text-xxs"
                :class="errorMessage ? 'pointer-events-auto cursor-help' : ''"
                :title="errorMessage"
                >{{ errorMessage }}</span
            >
        </div>

        <!-- Skelton -->
        <div class="skeleton:loader"></div>
    </div>
</template>

<script setup>
import { idGen } from '@utilities/idGen';

const props = defineProps({
    label: {
        type: [String, null],
        required: false,
        default: null,
    },
    placeholder: {
        type: [String, null],
        required: false,
        default: null,
    },
    disabled: {
        type: Boolean,
        required: false,
        default: false,
    },
    readonly: {
        type: Boolean,
        required: false,
        default: false,
    },
    vid: {
        type: String,
        required: false,
        default: () => idGen(6),
    },
    inputType: {
        type: String,
        required: false,
        default: 'text',
    },
    cubic: {
        type: Boolean,
        required: false,
        default: false,
    },
    square: {
        type: Boolean,
        required: false,
        default: false,
    },
    numeric: {
        type: Boolean,
        required: false,
        default: false,
    },
    /**
     * Let the validation message run past the field's own width instead of truncating inside it.
     * For narrow fields (a two-digit quantity box) the default `w-full truncate` leaves nothing
     * readable. Opt-in, because a wider message can overlap whatever sits beside the field.
     */
    wideError: {
        type: Boolean,
        required: false,
        default: false,
    },
    infoType: {
        type: [String, null],
        required: false,
        default: null,
    },
    infoTypeClass: {
        type: [String, null],
        required: false,
        default: null,
    },
    modelValue: {
        type: [String, Number, null],
        required: false,
        default: undefined,
    },
    autocomplete: {
        type: String,
        required: false,
        default: 'off',
    },
    autofocus: {
        type: Boolean,
        required: false,
        default: false,
    },
    small: {
        type: Boolean,
        required: false,
        default: false,
    },
    extraSmall: {
        type: Boolean,
        required: false,
        default: false,
    },
    maxlength: {
        type: Number,
        required: false,
        default: 1000,
    },
    updateOnInput: {
        type: Boolean,
        required: false,
        default: false,
    },
    censor: {
        type: Boolean,
        required: false,
        default: false,
    },
});

const validateNumeric = (value) => {
    // allow blanks
    if (!value) return true;

    if (isNaN(value) || isNaN(parseFloat(value))) {
        return 'Must be a valid number';
    }

    return true;
};

const vid = toRef(props, 'vid');
const {
    value: inputValue,
    errorMessage,
    handleBlur,
    handleChange,
} = useField(vid, props.numeric ? validateNumeric : undefined, {
    syncVModel: true,
});

const hasInputValue = computed(
    () => inputValue.value !== null && inputValue.value !== undefined && inputValue.value !== '',
);

function focusInput() {
    this.$refs.input.focus();
}

defineExpose({
    focusInput: focusInput,
});

const emits = defineEmits(['input:blur']);

const onBlur = (event) => {
    handleBlur(event);
    emits('input:blur', event);
};

const onFocus = (event) => {
    // Select all text on focus for number inputs so it can be easily overwritten
    if (props.inputType === 'number') {
        event.target.select();
    }
};

// Info type padding
const infoTypeEl = ref(null);
const measurementTrigger = ref(0);

const inputRightPaddingStyle = computed(() => {
    // increase this when the grid layout needs to re-run

    if (!props.infoType || props.cubic || props.square) {
        return {};
    }

    const width = infoTypeEl.value?.offsetWidth ?? 0;
    return { paddingRight: `${Math.ceil(width) - 10}px` };
});

onMounted(() =>
    nextTick(() => {
        measurementTrigger.value++;
    }),
);
</script>

<style scoped>
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active {
    transition: background-color 5000s ease-in-out 0s;
}

/* Ensure numeric input displays correctly on mobile */
.muval-number-input {
    appearance: textfield;
    -webkit-appearance: textfield;
    -moz-appearance: textfield;
}

/* Hide spinner buttons (up/down arrows) on number inputs in webkit browsers for cleaner appearance */
.muval-number-input::-webkit-outer-spin-button,
.muval-number-input::-webkit-inner-spin-button {
    appearance: none;
    -webkit-appearance: none;
    margin: 0;
}
</style>
